import { z } from 'zod';

// GET /activation-rules/blocks/:sku — por qué una regla de activación apagó
// un producto. Es lo que muestra la ficha.
//
// - 200 con `blocks: []` cuando ninguna regla lo apagó (o el SKU no existe):
//   no hay 404.
// - Ninguno, uno o los dos campos (`isActive`, `isWebActive`).
// - `ruleId`/`ruleName` en `null` cuando el motivo es `no-permit-matched`
//   (ninguna regla de Permitir lo deja activo: no hay una regla culpable).
// - `since`: inicio del apagado (ISO). Un cambio de motivo no la mueve.
//
// `target` y `reasonKind` se leen como string y no como enum a propósito: si
// el back suma un campo o un motivo nuevo, la ficha no se puede caer por eso.
// Un `target` desconocido no bloquea ningún campo; un `reasonKind` desconocido
// cae a un texto genérico (ver `activationBlockReasonText`).

export const ACTIVATION_BLOCK_TARGETS = ['isActive', 'isWebActive'] as const;
export type ActivationBlockTarget = (typeof ACTIVATION_BLOCK_TARGETS)[number];

export const ACTIVATION_BLOCK_REASON_KINDS = ['blocked-by', 'no-permit-matched'] as const;
export type ActivationBlockReasonKind = (typeof ACTIVATION_BLOCK_REASON_KINDS)[number];

// `explanation` (back 6d81202): por qué el juego vigente deja HOY apagado ese
// campo, condición por condición, con el valor consolidado de ahora y la
// prioridad de fuentes del juego.
// - `null` sin juego vigente, con un juego que no se puede evaluar o sin fila
//   viva del SKU; ausente con un back anterior. En los dos casos la ficha
//   muestra el motivo de siempre.
// - Una explicación que no se entiende cae a `null`: el motivo no se pierde.
// - `expected` es la lista de valores, el número o `null` según el operador;
//   `actual` es el valor del producto (`null` si no lo tiene, una lista si el
//   campo es de lista). Se leen como `unknown` y se formatean al mostrarlos.
export const ActivationBlockConditionSchema = z.object({
  pointer: z.string(),
  operator: z.string(),
  expected: z.unknown(),
  actual: z.unknown(),
  passed: z.boolean(),
});
export type ActivationBlockCondition = z.infer<typeof ActivationBlockConditionSchema>;

export const ActivationBlockExplanationSchema = z.object({
  evaluatedAt: z.string(),
  ruleSetId: z.number().int(),
  // `true`: las reglas vigentes ya no lo apagan; se suelta con el próximo
  // cambio del producto o el próximo barrido.
  allowedNow: z.boolean(),
  rules: z.array(
    z.object({
      ruleId: z.string(),
      ruleName: z.string(),
      list: z.string(),
      matched: z.boolean(),
      conditions: z.array(ActivationBlockConditionSchema),
    }),
  ),
});
export type ActivationBlockExplanation = z.infer<typeof ActivationBlockExplanationSchema>;

export const ActivationBlockSchema = z.object({
  target: z.string(),
  ruleSetId: z.number().int(),
  ruleId: z.string().nullable(),
  ruleName: z.string().nullable(),
  reasonKind: z.string(),
  since: z.string(),
  explanation: ActivationBlockExplanationSchema.nullable().optional().catch(null),
});
export type ActivationBlock = z.infer<typeof ActivationBlockSchema>;

// Cambio manual vivo de un campo (back 5467e65): `true`/`false` que una
// persona puso desde el admin. Mientras vale, las reglas no tocan ese campo;
// vence a las 10:00 de Bogotá (15:00 UTC) del día siguiente al cambio.
export const ManualOverrideSchema = z.object({
  target: z.string(),
  value: z.boolean(),
  expiresAt: z.string(),
});
export type ManualOverride = z.infer<typeof ManualOverrideSchema>;

export const ActivationBlocksResponseSchema = z.object({
  sku: z.string(),
  blocks: z.array(ActivationBlockSchema),
  // Ausente con un back anterior; una lista que no se entiende cae a `[]`.
  // Puede venir con `blocks: []`.
  manualOverrides: z.array(ManualOverrideSchema).optional().default([]).catch([]),
});
export type ActivationBlocksResponse = z.infer<typeof ActivationBlocksResponseSchema>;

// Filtro "Apagados por regla" del listado (`filters.blockedByActivationRule`
// en POST /products/query, /counts y /export). `any` = cualquiera de los dos
// campos, cada SKU una vez. Sale de los apagados por regla, no del valor del
// producto: un apagado a mano no entra.
export const BLOCKED_BY_ACTIVATION_RULE_FILTERS = ['any', 'isActive', 'isWebActive'] as const;
export const BlockedByActivationRuleFilterSchema = z.enum(BLOCKED_BY_ACTIVATION_RULE_FILTERS);
export type BlockedByActivationRuleFilter = z.infer<typeof BlockedByActivationRuleFilterSchema>;
